/**
 * Student Dashboard — "HoloLab Universe" dark / "Clean Academy" light
 */

import {
  BookOpen, CheckCircle, AlertCircle, Loader2, Sparkles,
  Play, Compass, ArrowRight, Dna, Beaker, Atom,
} from 'lucide-react';
import { UserProfileButton, useCurrentUser } from './AuthComponents';
import ThemeToggle from './guided/titration/panels/ThemeToggle';
import { useStudentExperiments, useJoinExperiment } from '../utils/convexHooks';
import { useNavigate } from 'react-router-dom';
import { useState } from 'react';

export default function StudentDashboard() {
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  const [classCode, setClassCode] = useState('');
  const [isJoining, setIsJoining] = useState(false);
  const [joinError, setJoinError] = useState('');
  const joinExperiment = useJoinExperiment();

  const handleJoinClass = async () => {
    if (classCode.trim().length !== 6 || !user) return;
    setIsJoining(true);
    setJoinError('');
    try {
      await joinExperiment({
        studentId: user._id,
        classCode: classCode.toUpperCase(),
        studentName: user.name || 'Unknown',
        studentEmail: user.email || '',
      });
      setClassCode('');
    } catch (error: any) {
      setJoinError(error.message || 'Invalid class code. Please check and try again.');
    } finally {
      setIsJoining(false);
    }
  };

  const firstName = user?.name?.split(' ')[0] || 'Scientist';
  const canJoin = classCode.length >= 6 && !isJoining;

  return (
    /* ── Root: light = #f0f4ff, dark = #030712 ── */
    <div className="min-h-screen bg-[#f0f4ff] dark:bg-[#030712] transition-colors duration-500 selection:bg-blue-200 dark:selection:bg-violet-900">

      {/* ── Background decorations ───────────────────────────────── */}
      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        {/* Light mode soft gradient */}
        <div className="dark:hidden absolute inset-0 bg-gradient-to-br from-blue-50/70 via-transparent to-teal-50/50" />
        {/* Dark mode aurora blobs */}
        <div className="hidden dark:block absolute -top-1/4 -right-1/4 w-[55vw] h-[55vw] rounded-full animate-aurora"
          style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.09) 0%, transparent 65%)' }} />
        <div className="hidden dark:block absolute top-1/2 -left-1/4 w-[40vw] h-[40vw] rounded-full animate-aurora2"
          style={{ background: 'radial-gradient(circle, rgba(6,182,212,0.06) 0%, transparent 65%)' }} />
        {/* Dark mode grid */}
        <div className="hidden dark:block absolute inset-0 opacity-[0.02]"
          style={{
            backgroundImage: `linear-gradient(rgba(139,92,246,0.8) 1px, transparent 1px), linear-gradient(90deg, rgba(139,92,246,0.8) 1px, transparent 1px)`,
            backgroundSize: '56px 56px',
          }} />
      </div>

      {/* ── Header ───────────────────────────────────────────────── */}
      <header className="sticky top-0 z-50 bg-white/80 dark:bg-[#030712]/80 backdrop-blur-xl border-b border-blue-100/80 dark:border-violet-500/10 transition-all">
        <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-3.5">
          <div className="flex items-center justify-between gap-4">
            <button onClick={() => navigate('/')} className="flex items-center gap-3 group focus:outline-none">
              <img src="/logo_only.png" alt="HoloLab" className="w-10 h-10 sm:w-11 sm:h-11 object-contain transition-transform duration-300 group-hover:scale-105" />
              <div className="hidden sm:block">
                <h1 className="text-xl font-bold text-slate-900 dark:text-white tracking-tight font-space leading-none">HoloLab</h1>
                <p className="text-[11px] font-semibold text-blue-500 dark:text-cyan-400 uppercase tracking-widest leading-none mt-0.5">Student Portal</p>
              </div>
            </button>
            <div className="flex items-center gap-2 bg-slate-100/80 dark:bg-white/5 p-1.5 rounded-full border border-slate-200/50 dark:border-violet-500/10 backdrop-blur-md">
              <ThemeToggle />
              <UserProfileButton />
            </div>
          </div>
        </div>
      </header>

      {/* ── Main ─────────────────────────────────────────────────── */}
      <main className="relative z-10 w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-8 xl:px-12 py-8 lg:py-10 animate-in slide-in-from-bottom-8 duration-700 ease-out">

        {/* Greeting */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-1">
            <Atom className="w-4 h-4 text-blue-500 dark:text-violet-400" />
            <span className="text-xs font-bold uppercase tracking-widest text-blue-500 dark:text-violet-400 font-space">Student Portal</span>
          </div>
          <h2 className="text-2xl sm:text-3xl font-bold font-space tracking-tight">
            <span className="text-slate-900 dark:text-white">Welcome back, </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-teal-500 dark:from-violet-400 dark:to-cyan-400">
              {firstName}
            </span>
            <span className="text-slate-900 dark:text-white">!</span>
          </h2>
          <p className="text-sm sm:text-base text-slate-500 dark:text-slate-400 mt-1">
            Ready for your next virtual discovery?
          </p>
        </div>

        {/* Content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-8 items-start">

          {/* ── Left: Join Lab card ───────────────────────────────── */}
          <div className="lg:col-span-4 lg:sticky lg:top-28">
            <div className="relative rounded-2xl p-6 sm:p-7 overflow-hidden transition-all duration-300
              bg-white dark:bg-[#0d0d1a]
              border border-slate-200 dark:border-violet-500/18
              shadow-sm dark:shadow-[0_0_40px_rgba(139,92,246,0.07)]">

              {/* Decorative blob — visible in both modes, colour differs */}
              <div
                className="absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl pointer-events-none"
                style={{ background: 'radial-gradient(circle, rgba(139,92,246,0.10) 0%, transparent 70%)' }}
              />

              <div className="relative z-10">
                {/* Icon */}
                <div className="w-11 h-11 rounded-xl flex items-center justify-center mb-5
                  bg-blue-100 dark:bg-violet-900/40
                  border border-blue-200 dark:border-violet-500/20">
                  <Sparkles className="w-5 h-5 text-blue-600 dark:text-violet-400" />
                </div>

                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-1.5 font-space">Join a Lab</h3>
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-5 leading-relaxed">
                  Enter the 6-character code from your instructor to join an experiment.
                </p>

                {/* Input + button */}
                <div className="flex flex-col gap-3">
                  <div className="relative">
                    <input
                      type="text"
                      value={classCode}
                      onChange={(e) => setClassCode(e.target.value.toUpperCase())}
                      placeholder="e.g. LAB123"
                      className="w-full pl-4 pr-12 py-3.5 rounded-xl font-bold font-mono tracking-widest text-center text-sm uppercase
                        placeholder:font-sans placeholder:tracking-normal placeholder:font-normal
                        placeholder:text-slate-400 dark:placeholder:text-slate-600
                        border-2 transition-all duration-200 focus:outline-none
                        bg-slate-50 dark:bg-[#12121f]
                        text-slate-900 dark:text-white
                        border-slate-200 dark:border-violet-500/25
                        focus:border-blue-500 dark:focus:border-violet-500
                        focus:ring-4 focus:ring-blue-500/10 dark:focus:ring-violet-500/15"
                      maxLength={6}
                      disabled={isJoining}
                    />
                    <div className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500 pointer-events-none">
                      <Compass className="w-4 h-4" />
                    </div>
                  </div>

                  {/* ── Join button — pure Tailwind dark: variants ── */}
                  <button
                    onClick={handleJoinClass}
                    disabled={!canJoin}
                    className={`w-full py-3.5 rounded-xl font-semibold text-sm font-space text-white
                      flex items-center justify-center gap-2
                      transition-all duration-300
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${canJoin ? 'hover:-translate-y-0.5' : ''}
                      bg-blue-600 hover:bg-blue-700
                      dark:bg-gradient-to-r dark:from-violet-600 dark:to-cyan-500
                      dark:hover:from-violet-500 dark:hover:to-cyan-400
                      shadow-md shadow-blue-500/20 dark:shadow-violet-500/20
                      ${canJoin ? 'hover:shadow-lg hover:shadow-blue-500/30 dark:hover:shadow-violet-500/30' : 'shadow-none'}`}
                  >
                    {isJoining ? (
                      <><Loader2 className="w-4 h-4 animate-spin" /> Synchronizing…</>
                    ) : (
                      <><Compass className="w-4 h-4" /> Access Lab</>
                    )}
                  </button>
                </div>

                {joinError && (
                  <div className="mt-3 p-3 rounded-xl flex items-start gap-2 text-sm
                    bg-red-50 dark:bg-red-900/15
                    border border-red-200 dark:border-red-800/30
                    text-red-600 dark:text-red-400">
                    <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                    <p className="leading-tight">{joinError}</p>
                  </div>
                )}

                {/* ── Sandbox Play Access ── */}
                <div className="mt-8 pt-6 border-t border-slate-200 dark:border-slate-800">
                  <div className="flex items-start gap-4 p-4 rounded-2xl bg-gradient-to-br from-fuchsia-50 to-indigo-50 dark:from-fuchsia-900/10 dark:to-indigo-900/10 border border-fuchsia-100 dark:border-fuchsia-500/20 transition-all hover:shadow-md hover:border-fuchsia-300 dark:hover:border-fuchsia-500/40">
                    <div className="p-3 rounded-xl bg-white dark:bg-[#0B1120] text-fuchsia-600 dark:text-fuchsia-400 shadow-sm border border-fuchsia-100 dark:border-fuchsia-500/20">
                      <Atom className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-bold text-slate-900 dark:text-white mb-1">Chemistry Sandbox</h4>
                      <p className="text-xs text-slate-600 dark:text-slate-400 mb-3 leading-relaxed">Freely mix elements and discover reactions without a guided lab procedure.</p>
                      <button 
                        onClick={() => navigate('/play')}
                        className="text-xs font-bold px-4 py-2 rounded-lg bg-fuchsia-600 hover:bg-fuchsia-700 text-white transition-colors shadow-sm flex items-center justify-center gap-1.5"
                      >
                        <Play className="w-3.5 h-3.5" /> Enter Sandbox
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ── Right: Experiments list ───────────────────────────── */}
          <div className="lg:col-span-8 space-y-5">
            <div className="flex items-center justify-between">
              <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white flex items-center gap-2 font-space">
                <Beaker className="w-5 h-5 text-blue-500 dark:text-violet-400" />
                Active Experiments
              </h3>
              <span className="text-xs font-semibold font-space
                text-slate-500 dark:text-slate-400
                bg-slate-100 dark:bg-white/5
                border border-slate-200 dark:border-white/8
                px-3 py-1 rounded-full">
                Lab Journal
              </span>
            </div>
            <MyExperimentsTab />
          </div>
        </div>
      </main>
    </div>
  );
}

// ── Experiment Cards ──────────────────────────────────────────────────────
function MyExperimentsTab() {
  const { user } = useCurrentUser();
  const experiments = useStudentExperiments(user?._id);
  const navigate = useNavigate();

  if (experiments === undefined) {
    return (
      <div className="flex flex-col items-center justify-center py-20 gap-4">
        <div className="relative">
          <div className="absolute inset-0 bg-blue-500/10 dark:bg-violet-500/20 blur-xl rounded-full" />
          <Loader2 className="w-8 h-8 text-blue-500 dark:text-violet-400 animate-spin relative z-10" />
        </div>
        <p className="text-xs font-bold text-slate-400 dark:text-slate-500 tracking-widest uppercase animate-pulse font-space">
          Loading Labs…
        </p>
      </div>
    );
  }

  if (!experiments || experiments.length === 0) {
    return (
      <div className="rounded-2xl p-10 sm:p-12 text-center
        bg-white dark:bg-white/[0.03]
        border border-dashed border-slate-300 dark:border-violet-500/15">
        <div className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-4
          bg-slate-100 dark:bg-white/5
          border border-slate-200 dark:border-white/8">
          <BookOpen className="w-7 h-7 text-slate-400 dark:text-slate-500" />
        </div>
        <h3 className="text-base font-bold text-slate-900 dark:text-white mb-2 font-space">Empty Journal</h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 max-w-xs mx-auto leading-relaxed">
          You haven't joined any experiments yet. Use the code from your instructor to begin.
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
      {experiments.map((item) => {
        const exp = item.experiment;
        const progress = item.progress;
        if (!exp) return null;

        const steps = {
          preLab: progress?.status === 'pre-lab' || progress?.preLab?.passed
            ? 'completed' : progress?.status === 'not-started' ? 'pending' : 'in-progress',
          inLab: progress?.status === 'in-lab' || progress?.labData?.completedAt
            ? 'completed' : progress?.status === 'post-lab' || progress?.status === 'completed'
            ? 'completed' : 'pending',
          postLab: progress?.status === 'completed'
            ? 'completed' : progress?.status === 'post-lab' ? 'in-progress' : 'pending',
        };

        const completedSteps = Object.values(steps).filter(s => s === 'completed').length;
        const pct = Math.round((completedSteps / 3) * 100);
        const isCompleted = progress?.status === 'completed';

        return (
          <div
            key={exp._id}
            className="group relative flex flex-col rounded-2xl overflow-hidden
              bg-white dark:bg-[#0d0d1a]
              border border-slate-200/80 dark:border-violet-500/10
              shadow-sm hover:shadow-xl hover:shadow-blue-500/8 dark:hover:shadow-violet-500/12
              hover:-translate-y-1 transition-all duration-300"
          >
            {/* Top accent bar */}
            <div className={`h-0.5 w-full transition-all duration-500 ${
              isCompleted
                ? 'bg-gradient-to-r from-emerald-400 to-teal-400 opacity-100'
                : 'bg-gradient-to-r from-blue-500 to-teal-400 dark:from-violet-500 dark:to-cyan-400 opacity-0 group-hover:opacity-100'
            }`} />

            {/* Watermark icon */}
            <div className="absolute top-4 right-4 pointer-events-none opacity-[0.04] group-hover:opacity-[0.07] transition-opacity">
              <Dna className="w-20 h-20 text-slate-700 dark:text-violet-400" />
            </div>

            <div className="relative z-10 p-5 sm:p-6 flex flex-col flex-1 gap-4">

              {/* Title + status */}
              <div className="flex items-start justify-between gap-3">
                <h3 className="text-base sm:text-lg font-bold text-slate-900 dark:text-white leading-tight font-space flex-1 min-w-0 truncate">
                  {exp.title}
                </h3>
                <div className="shrink-0">
                  {isCompleted ? (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold
                      bg-emerald-100 dark:bg-emerald-900/25
                      text-emerald-700 dark:text-emerald-400
                      border border-emerald-200 dark:border-emerald-500/20">
                      <CheckCircle className="w-3 h-3" /> Done
                    </span>
                  ) : (
                    <span className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11px] font-bold
                      bg-amber-100 dark:bg-amber-900/20
                      text-amber-700 dark:text-amber-400
                      border border-amber-200 dark:border-amber-500/20">
                      <Play className="w-2.5 h-2.5 fill-current" /> Active
                    </span>
                  )}
                </div>
              </div>

              {/* Class code pill */}
              <span className="self-start px-2.5 py-1 rounded-lg font-mono font-bold tracking-widest text-[10px] uppercase
                bg-slate-100 dark:bg-white/5
                text-slate-500 dark:text-slate-400
                border border-slate-200 dark:border-white/8">
                {exp.classCode}
              </span>

              {/* Progress block */}
              <div className="rounded-xl p-4
                bg-slate-50 dark:bg-white/[0.03]
                border border-slate-100 dark:border-violet-500/8">
                <div className="flex items-center justify-between mb-2.5 text-xs font-bold uppercase tracking-wider">
                  <span className="text-slate-500 dark:text-slate-400 font-space">Progress</span>
                  <span className={isCompleted
                    ? 'text-emerald-600 dark:text-emerald-400'
                    : 'text-blue-600 dark:text-violet-400'}>
                    {pct}%
                  </span>
                </div>

                {/* Bar */}
                <div className="h-1.5 w-full rounded-full overflow-hidden mb-2.5
                  bg-slate-200 dark:bg-white/8">
                  <div
                    className={`h-full rounded-full transition-all duration-1000 ease-out ${
                      isCompleted
                        ? 'bg-gradient-to-r from-emerald-400 to-teal-400'
                        : 'bg-gradient-to-r from-blue-500 to-teal-400 dark:from-violet-500 dark:to-cyan-400'
                    }`}
                    style={{ width: `${pct}%` }}
                  />
                </div>

                {/* Stage labels */}
                <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest font-space">
                  {(['Pre-Lab', 'Sim', 'Review'] as const).map((label, i) => {
                    const key = ['preLab', 'inLab', 'postLab'][i] as keyof typeof steps;
                    const state = steps[key];
                    return (
                      <span key={label} className={
                        state === 'completed'   ? 'text-emerald-600 dark:text-emerald-400'
                        : state === 'in-progress' ? 'text-blue-600 dark:text-violet-400'
                        : 'text-slate-400 dark:text-slate-600'
                      }>
                        {label}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* CTA button */}
              <button
                onClick={() => {
                  if (isCompleted) {
                    navigate(`/student/results?id=${exp._id}`);
                  } else {
                    let path = '/play';
                    if (exp.experimentType === 'znH2SO4') path = `/play/experiment/zinc-h2so4?id=${exp._id}`;
                      else if (exp.experimentType === 'titration') path = `/play/experiment/titration-hcl-naoh?id=${exp._id}`;
                      else if (exp.experimentType === 'naphthalene') path = `/play/experiment/naphthalene?id=${exp._id}`;
                      else if (exp.experimentType === 'solubility') path = `/play/experiment/solubility?id=${exp._id}`;
                      else if (exp.experimentType === 'magnesiumOxide') path = `/play/experiment/magnesium-oxide?id=${exp._id}`;
                      else if (exp.experimentType === 'copperOxide') path = `/play/experiment/copper-oxide?id=${exp._id}`;
                      else if (exp.experimentType === 'balancedEquations') path = `/play/experiment/balanced-equations?id=${exp._id}`;
                    navigate(path);
                  }
                }}
                className={`mt-auto w-full flex items-center justify-center gap-2 py-3 rounded-xl font-semibold text-sm font-space transition-all duration-300 group/btn ${
                  isCompleted
                    ? `bg-slate-100 dark:bg-white/8
                       text-slate-700 dark:text-slate-300
                       hover:bg-slate-200 dark:hover:bg-white/12
                       border border-slate-200 dark:border-white/10`
                    : `bg-blue-50 dark:bg-violet-500/10
                       text-blue-700 dark:text-violet-300
                       border border-blue-200 dark:border-violet-500/20
                       hover:bg-blue-600 dark:hover:bg-violet-600
                       hover:text-white dark:hover:text-white
                       hover:border-blue-600 dark:hover:border-violet-600
                       hover:shadow-lg hover:shadow-blue-500/20 dark:hover:shadow-violet-500/20`
                }`}
              >
                {isCompleted ? (
                  <>View Final Report <ArrowRight className="w-4 h-4 group-hover/btn:translate-x-0.5 transition-transform" /></>
                ) : (
                  <>Resume Simulation <Play className="w-3.5 h-3.5 fill-current group-hover/btn:translate-x-0.5 transition-transform" /></>
                )}
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
}
